import mapValues from "lodash/mapValues";
import sortBy from "lodash/sortBy";
import { SoftwareInfo } from "@sscs/types";
import { FILES_ENDPOINT, FILE_SOFTWARE_INFO_DATA } from "Constants";
import {
  GenerateSoftwareInfoRequest,
  GenerateSoftwareInfoResponse,
  GroupUpdatesMessage,
  PortalFile,
  ReprocessFileRequest,
  SoftwareInfoChoices,
  UploadConfig,
  UrlImportCheckResponse,
  UrlImportData,
  ValidateUrlRequest,
  ValidateUrlResponse,
} from "Types";
import { baseApi } from "./base.api";
import { listenToGroupUpdates } from "./websocket";

// The import/check endpoint sends "" for fields it could not resolve. The
// wizards spread this object over the form defaults, so every empty field
// must fall back to its default here or it wipes one (an empty architecture
// used to block every P/URL import at the software-info step).
export const withSoftwareInfoDefaults = (info: SoftwareInfo): SoftwareInfo => ({
  ...info,
  publisher: info.publisher || FILE_SOFTWARE_INFO_DATA.publisher,
  version: info.version || FILE_SOFTWARE_INFO_DATA.version,
  category: info.category || FILE_SOFTWARE_INFO_DATA.category,
  license: info.license || FILE_SOFTWARE_INFO_DATA.license,
  platform: info.platform || FILE_SOFTWARE_INFO_DATA.platform,
  architecture: info.architecture || FILE_SOFTWARE_INFO_DATA.architecture,
});

export const filesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    uploadConfig: build.query<UploadConfig, void>({
      query: () => `${FILES_ENDPOINT}/upload/config`,
    }),
    validateUrlImport: build.query<ValidateUrlResponse, ValidateUrlRequest>({
      query: (queryArg) => ({
        url: `${FILES_ENDPOINT}/import/validate`,
        params: {
          import_string: queryArg.url,
        },
        method: "GET",
      }),
    }),
    checkUrlImport: build.query<UrlImportCheckResponse, UrlImportData>({
      query: (queryArg) => ({
        url: `${FILES_ENDPOINT}/import/check`,
        body: {
          import_string: queryArg.url,
          username: queryArg.username,
          password: queryArg.password,
          token: queryArg.token,
        },
        method: "POST",
      }),
      transformResponse: (data: UrlImportCheckResponse) => ({
        ...data,
        software_info:
          data.software_info && withSoftwareInfoDefaults(data.software_info),
      }),
    }),
    getSingleFile: build.query<PortalFile, string>({
      query: (fileId) => `${FILES_ENDPOINT}/file/${fileId}`,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          if (
            message.entity === "filestream_file" &&
            message.action === "updated"
          ) {
            updateCachedData((draft) => {
              const updatedFile = message.data.find((file) => file.id === arg);

              return updatedFile !== undefined ? updatedFile : draft;
            });
          }
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
      providesTags: (result, error, fileId) => [{ type: "Files", id: fileId }],
    }),
    reprocessFile: build.mutation<void, ReprocessFileRequest>({
      query(queryArg) {
        return {
          url: `${FILES_ENDPOINT}/file/${queryArg.fileId}/reprocess`,
          params: {
            notify_on_completion: queryArg.notify_on_completion,
          },
          method: "POST",
        };
      },
      invalidatesTags: (result, error, queryArg) => [
        { type: "Files", id: queryArg.fileId },
      ],
    }),
    softwareInfoChoices: build.query<SoftwareInfoChoices, void>({
      query: () => `${FILES_ENDPOINT}/software-info-choices`,
      transformResponse: (data: SoftwareInfoChoices) => ({
        category_options: sortBy(data.category_options, [
          (option) => option.toLowerCase(),
        ]),
        license_options: sortBy(data.license_options, [
          (option) => option.license.toLowerCase(),
        ]),
        platform_options: sortBy(data.platform_options, [
          (option) => option.toLowerCase(),
        ]),
        architecture_options_by_platform: mapValues(
          data.architecture_options_by_platform,
          (options) => sortBy(options, [(option) => option.toLowerCase()]),
        ),
      }),
    }),
    generateSoftwareInfo: build.mutation<
      GenerateSoftwareInfoResponse,
      GenerateSoftwareInfoRequest
    >({
      query: (queryArg) => ({
        url: `${FILES_ENDPOINT}/software-info/generate`,
        body: queryArg,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadConfigQuery,
  useLazyValidateUrlImportQuery,
  useLazyCheckUrlImportQuery,
  useGetSingleFileQuery,
  useReprocessFileMutation,
  useSoftwareInfoChoicesQuery,
  useLazySoftwareInfoChoicesQuery,
  useGenerateSoftwareInfoMutation,
} = filesApi;
